/**
 * M.P.M. (Marta Projects Manager) Configuration
 *
 * Agent configuration and metadata for project intelligence
 */

import type { AIAgent } from '../types.js';

export const MPM_AGENT_ID = 'mpm';
export const MPM_AGENT_VERSION = '1.0.0';

export const MPM_AGENT: AIAgent = {
  id: MPM_AGENT_ID,
  name: 'Marta Projects Manager',
  description: 'Monitors projects, suggests priorities, detects completion and proposes actions',
  version: MPM_AGENT_VERSION,
  enabled: true,

  capabilities: [
    {
      id: 'analyze-project-health',
      name: 'Analyze Project Health',
      description: 'Evaluates project status based on task progress, deadlines and milestones',
      inputSchema: {
        type: 'object',
        properties: {
          projectId: { type: 'string', description: 'Project ID to analyze' },
        },
        required: ['projectId'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          healthScore: { type: 'number', minimum: 0, maximum: 100 },
          risk: { type: 'string', enum: ['ok', 'warning', 'critical'] },
          issues: { type: 'array', items: { type: 'string' } },
          suggestions: { type: 'array', items: { type: 'string' } },
        },
        required: ['healthScore', 'risk'],
      },
    },
    {
      id: 'suggest-task-priority',
      name: 'Suggest Task Priority',
      description: 'Analyzes task context to suggest optimal priority and ordering',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: { type: 'string', description: 'Task ID' },
          projectId: { type: 'string', description: 'Parent project ID' },
        },
        required: ['taskId'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          suggestedPriority: { type: 'string', enum: ['bassa', 'media', 'alta', 'urgente'] },
          reason: { type: 'string' },
          confidence: { type: 'number', minimum: 0, maximum: 1 },
        },
      },
    },
    {
      id: 'detect-project-completion',
      name: 'Detect Project Completion',
      description: 'Checks if all tasks are done and proposes marking project as completed',
      inputSchema: {
        type: 'object',
        properties: {
          projectId: { type: 'string', description: 'Project ID' },
        },
        required: ['projectId'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          isComplete: { type: 'boolean' },
          completionPercentage: { type: 'number' },
          remainingTasks: { type: 'number' },
          proposedAction: { type: 'string' },
        },
      },
    },
    {
      id: 'estimate-timeline',
      name: 'Estimate Timeline',
      description: 'Estimates project completion date based on current velocity',
      inputSchema: {
        type: 'object',
        properties: {
          projectId: { type: 'string', description: 'Project ID' },
        },
        required: ['projectId'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          estimatedCompletionDate: { type: 'string' },
          velocityPerWeek: { type: 'number' },
          isOnTrack: { type: 'boolean' },
          daysRemaining: { type: 'number' },
        },
      },
    },
  ],

  workflows: ['project-completion-check', 'task-priority-suggestion', 'milestone-tracking'],
  triggers: [
    'entity.created/task',
    'entity.updated/task',
    'entity.deleted/task',
    'entity.updated/project',
    'entity.created/milestone',
    'entity.updated/milestone',
    'entity.created/time_entry',
  ],

  metrics: {
    totalCalls: 0,
    successRate: 0,
    avgLatencyMs: 0,
    correctionRate: 0,
  },

  trainingDataCount: 0,
  lastTrainedAt: undefined,
};

export const PROJECT_HEALTH_PROMPT = `You are a project management assistant. Analyze the project data and return a JSON with:
- healthScore: 0-100 indicating overall project health
- risk: "ok" (on track), "warning" (minor issues), "critical" (major blockers)
- issues: array of identified problems
- suggestions: array of actionable suggestions

Criteria:
1. Task completion rate vs timeline
2. Overdue tasks count and severity
3. Milestone progress
4. Time tracking accuracy (estimated vs logged)
5. Team workload distribution

Respond ONLY with a valid JSON object, no other text.`;

export const MPM_SETTINGS = {
  /** Model to use for analysis */
  model: 'llama-3.3-70b-versatile',
  /** Maximum tokens for response */
  maxTokens: 500,
  /** Health check interval in hours */
  healthCheckIntervalHours: 24,
  /** Minimum task count for meaningful analysis */
  minTasksForAnalysis: 3,
  /** Overdue threshold in days before warning */
  overdueDaysWarning: 2,
  /** Overdue threshold in days before critical */
  overdueDaysCritical: 7,
};
